import {ApiClient} from '@adyenPWA/lib/services/api'

export class AdyenPaymentsService {
    baseUrl = '/api/adyen/payments'
    apiClient = null

    /**
     * Initializes a new instance of the payments service with authentication and site context.
     * @constructor
     * @param {string} token - The API authentication token used for secure requests.
     * @param {string} site - The site identifier for which the API client is configured.
     */
    constructor(token, customerId, basketId, site) {
        this.apiClient = new ApiClient(this.baseUrl, token, customerId, basketId, site)
    }


    /**
     * Submits payment data to the Adyen payment service for processing.
     * @param {Object} adyenStateData - The payment state data object required by Adyen.
     * @param {string} basketId - The unique identifier of the basket associated with the payment.
     * @param {string} customerId - The unique identifier of the customer making the payment.
     * @returns {Promise<Object>} - Resolves with the response from the Adyen payment service.
     * @throws {Error} Throws an error if the payment service responds with a status code >= 300.
     */
    async submitPayment(adyenStateData, locale) {
        const ipResponse = await fetch('/ip', {
            method: 'GET'
        })
        const ipData = await ipResponse.json()

        // Fetch the client's user agent
        const userAgentResponse = await fetch('/user-agent', {
            method: 'GET'
        })
        const userAgentData = await userAgentResponse.json()

        const res = await this.apiClient.post({
            body: JSON.stringify({
                data: {
                    ...adyenStateData,
                    ip: ipData?.clientIp,
                    userAgent: userAgentData?.userAgent,
                    riskifiedBeacon: adyenStateData?.riskifiedBeacon
                }
            }),
            queryParams: {locale: locale?.id}
        })

        const rawBody = await res.text()
        let parsedBody
        try {
            parsedBody = rawBody ? JSON.parse(rawBody) : {}
        } catch (parseError) {
            const contentType = res.headers?.get?.('content-type')
            throw new Error(
                `Adyen payments response was not valid JSON (status ${res.status}, content-type ${contentType}): ${rawBody?.slice(
                    0,
                    500
                )}`
            )
        }

        if (res.status >= 300) {
            const err = new Error(
                parsedBody?.errorMessage || `Payment failed with status ${res.status}`
            )
            if (parsedBody?.newBasketId) {
                err.newBasketId = parsedBody.newBasketId
            }
            throw err
        }
        return parsedBody
    }
}
